import { createApi } from '@reduxjs/toolkit/query/react';
import { apiEndpoints } from 'Utils/ApiEndpoints';
import { createAuthBaseQuery } from './baseQuery';

export const userApi = createApi({
    reducerPath: 'userApi',
    baseQuery: createAuthBaseQuery(),
    tagTypes: ['User', 'UserReport'],
    endpoints: (builder) => ({
        getUserData: builder.query({
            query: ({ page, limit, searchData }) => ({
                url: `${apiEndpoints?.userGetUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['User'],
        }),
        getUserDataDetails: builder.query({
            query: (userId) => ({
                url: `${apiEndpoints?.userDetailsUrl}?user_id=${userId}`,
                method: 'GET',
            }),
            providesTags: ['User'],
        }),
        updateUserStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.userStatusUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['User'],
        }),
        getDashboardData: builder.query({
            query: (pastDay) => ({
                url: `${apiEndpoints?.userDashboardUrl}${pastDay ? `?past_day=${pastDay}` : ''}`,
                method: 'GET',
            }),
        }),

        getDashboardReportData: builder.query({
            query: (pastDay) => ({
                url: `${apiEndpoints?.userDashboardReportUrl}${pastDay ? `?past_day=${pastDay}` : ''}`,
                method: 'GET',
            }),
        }),

        getSubscriptionChartData: builder.query({
            query: (pastDay) => ({
                url: `${apiEndpoints?.subscriptionChartUrl}${pastDay ? `?past_day=${pastDay}` : ''}`,
                method: 'GET',
            }),
        }),

        // 1. Get All Categories
        getCategoryData: builder.query({
            query: ({ page, limit, searchData }) => ({
                url: `${apiEndpoints?.getCategoryUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['Category'],
        }),

        // 2. Add Category
        addCategory: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addCategoryUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['Category'],
        }),

        // 3. Update Category (Name or Status)
        updateCategory: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updateCategoryUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['Category'],
        }),

        // 4. Delete Category
        deleteCategory: builder.mutation({
            query: (id) => ({
                url: `${apiEndpoints?.deleteCategoryUrl}?category_id=${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ['Category'],
        }),

        // ================== BREED MAP API ==================
        getBreedsData: builder.query({
            query: ({ page, limit, searchData, categoryId }) => ({
                url: `${apiEndpoints?.getBreedsUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}&categoryId=${categoryId}`,
                method: 'GET',
            }),
            providesTags: ['Breed'],
        }),
        addBreed: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addBreedUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['Breed'],
        }),
        updateBreed: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updateBreedUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['Breed'],
        }),
        deleteBreed: builder.mutation({
            query: (id) => ({
                url: `${apiEndpoints?.deleteBreedUrl}?breed_id=${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ['Breed'],
        }),

        // ================== HAIR CUT STYLE API ==================
        getStylesData: builder.query({
            query: ({ page, limit, searchData, categoryId }) => ({
                url: `${apiEndpoints?.getStylesUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}&category_id=${categoryId}`,
                method: 'GET',
            }),
            providesTags: ['Style'],
        }),
        addStyle: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addStyleUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['Style'],
        }),
        updateStyle: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updateStyleUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['Style'],
        }),
        deleteStyle: builder.mutation({
            query: (id) => ({
                url: `${apiEndpoints?.deleteStyleUrl}?style_id=${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ['Style'],
        }),

        /* ================== BACKGROUND IMAGE API ================== */
        getBgImagesData: builder.query({
            // Added categoryId to parameters
            query: ({ page, limit, searchData, categoryId }) => ({
                url: `${apiEndpoints?.getBgImagesUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}&category_id=${categoryId ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['BackgroundImage'],
        }),

        addBgImage: builder.mutation({
            query: (formData) => ({
                url: apiEndpoints?.addBgImageUrl,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ['BackgroundImage'],
        }),

        updateBgImage: builder.mutation({
            query: (formData) => ({
                url: apiEndpoints?.updateBgImageUrl,
                method: "PUT",
                body: formData,
            }),
            invalidatesTags: ['BackgroundImage'],
        }),

        changeBgImageStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.changeBgImageStatusUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['BackgroundImage'],
        }),

        deleteBgImage: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.deleteBgImageUrl,
                method: "DELETE",
                body: data,
            }),
            invalidatesTags: ['BackgroundImage'],
        }),

        getBackgroundCategories: builder.query({
            query: ({ page, limit, searchData }) => ({
                url: `${apiEndpoints?.getBgCategoryUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['BackgroundCategory'],
        }),

        addBackgroundCategory: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addBgCategoryUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['BackgroundCategory'],
        }),

        addBackgroundSubCategorySorting: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addBackgroundSubCategorySortingUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['BackgroundCategory'],
        }),

        updateBackgroundCategory: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updateBgCategoryUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['BackgroundCategory'],
        }),

        // Specific endpoint for status change as per your request
        changeBgCategoryStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.changeBgCategoryStatusUrl,
                method: "PUT", // Assuming PUT, check backend if it expects POST
                body: data
            }),
            invalidatesTags: ['BackgroundCategory'],
        }),

        deleteBackgroundCategory: builder.mutation({
            query: (id) => ({
                url: `${apiEndpoints?.deleteBgCategoryUrl}`, // Assuming query param based on pattern
                method: "DELETE",
                body: {
                    catId: id
                }
            }),
            invalidatesTags: ['BackgroundCategory'],
        }),

        getBackgroundCategoryDetails: builder.query({
            query: (id) => ({
                url: `${apiEndpoints?.getBgCategoryDetailsUrl}?category_id=${id}`,
                method: 'GET',
            }),
        }),

        /* ================== STICKER API ================== */
        getStickersData: builder.query({
            query: ({ page, limit, searchData }) => ({
                url: `${apiEndpoints?.getStickersUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['Sticker'],
        }),

        addSticker: builder.mutation({
            query: (formData) => ({
                url: apiEndpoints?.addStickerUrl,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ['Sticker'],
        }),

        updateSticker: builder.mutation({
            query: (formData) => ({
                url: apiEndpoints?.updateStickerUrl,
                method: "PUT",
                body: formData,
            }),
            invalidatesTags: ['Sticker'],
        }),

        changeStickerStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.changeStickerStatusUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['Sticker'],
        }),

        deleteSticker: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.deleteStickerUrl,
                method: "DELETE",
                body: data,
            }),
            invalidatesTags: ['Sticker'],
        }),

        // Add these inside the endpoints: (builder) => ({ ... })
        getReportList: builder.query({
            query: ({ page, limit, type }) => ({
                url: `${apiEndpoints?.reportListUrl}?page=${page}&limit=${limit}${type ? `&type=${type}` : ''}`,
                method: 'GET',
            }),
            providesTags: ['Report'],
        }),
        getReportDetails: builder.query({
            query: (reportId) => ({
                url: `${apiEndpoints?.reportDetailsUrl}?reportId=${reportId}`,
                method: 'GET',
            }),
            providesTags: ['Report'],
        }),
        deleteReport: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.reportDeleteUrl,
                method: "DELETE",
                body: data
            }),
            invalidatesTags: ['Report'],
        }),
        acceptReport: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.reportAcceptUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['Report'],
        }),
        rejectReport: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.reportRejectUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['Report'],
        }),
        getUserReportList: builder.query({
            query: ({ page, limit, search, reviewStatus, type }) => ({
                url: `${apiEndpoints?.userReportListUrl}?page=${page}&limit=${limit}&search=${encodeURIComponent(search ?? '')}&review_status=${reviewStatus ?? ''}&type=${type ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['UserReport'],
        }),
        getUserReportDetails: builder.query({
            query: (userId) => ({
                url: `${apiEndpoints?.userReportDetailsUrl}?user_id=${userId}`,
                method: 'GET',
            }),
            providesTags: ['UserReport'],
        }),
        userReportAction: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.userReportActionUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['UserReport', 'User'],
        }),
        deleteUserReport: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.userReportDeleteUrl,
                method: "DELETE",
                body: data
            }),
            invalidatesTags: ['UserReport', 'User'],
        }),
        /* ================== CONTACT US API ================== */
        getContactList: builder.query({
            query: ({ page, limit, searchData }) => ({
                url: `${apiEndpoints?.contactUsListUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['ContactUs'],
        }),

        deleteContact: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.contactUsDeleteUrl,
                method: "DELETE",
                body: data,
            }),
            invalidatesTags: ['ContactUs'],
        }),

        replyContact: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.contactUsReplyUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['ContactUs'],
        }),

        // 1. Get Sticker Categories
        getStickerCategories: builder.query({
            query: ({ page, limit, searchData }) => ({
                url: `${apiEndpoints?.getStickerCategoryUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['StickerCategory'],
        }),

        // 2. Add Sticker Category
        addStickerCategory: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addStickerCategoryUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['StickerCategory'],
        }),

        // 3. Update Sticker Category
        updateStickerCategory: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updateStickerCategoryUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['StickerCategory'],
        }),

        // 4. Change Status
        changeStickerCategoryStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.changeStickerCategoryStatusUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['StickerCategory'],
        }),

        // 5. Delete Sticker Category
        deleteStickerCategory: builder.mutation({
            query: (id) => ({
                url: `${apiEndpoints?.deleteStickerCategoryUrl}`,
                method: "DELETE",
                body: {
                    catId: id
                }
            }),
            invalidatesTags: ['StickerCategory'],
        }),

        // 6. Sorting (Optional/If supported)
        addStickerCategorySorting: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addStickerSubCategorySortingUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['StickerCategory'],
        }),

        // 1. Get Sticker Prompts (Filtered by Category ID)
        getStickerPrompts: builder.query({
            query: ({ page, limit, searchData, categoryId }) => ({
                url: `${apiEndpoints?.getStickerPromptsUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}&category_id=${categoryId}`,
                method: 'GET',
            }),
            providesTags: ['StickerPrompt'],
        }),

        // 2. Add Sticker Prompt
        addStickerPrompt: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addStickerPromptUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['StickerPrompt'],
        }),

        // 3. Update Sticker Prompt
        updateStickerPrompt: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updateStickerPromptUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['StickerPrompt'],
        }),

        // 4. Change Status
        changeStickerPromptStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.changeStickerPromptStatusUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['StickerPrompt'],
        }),

        // 5. Delete Sticker Prompt
        deleteStickerPrompt: builder.mutation({
            query: (promptId) => ({
                url: apiEndpoints?.deleteStickerPromptUrl,
                method: "DELETE",
                body: { promptId }
            }),
            invalidatesTags: ['StickerPrompt'],
        }),

        // 6. Get Single Prompt Details
        getStickerPromptDetails: builder.query({
            query: (promptId) => ({
                url: `${apiEndpoints?.getStickerPromptDetailsUrl}?promptId=${promptId}`,
                method: 'GET',
            }),
        }),

        getBgPromptCategories: builder.query({
            query: ({ page, limit, searchData }) => ({
                url: `${apiEndpoints?.getBgPromptCategoryListUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['BgPromptCategory'],
        }),

        addBgPromptCategory: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addBgPromptCategoryUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['BgPromptCategory'],
        }),

        updateBgPromptCategory: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updateBgPromptCategoryUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['BgPromptCategory'],
        }),

        changeBgPromptCategoryStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.changeBgPromptCategoryStatusUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['BgPromptCategory'],
        }),

        deleteBgPromptCategory: builder.mutation({
            query: (id) => ({
                url: apiEndpoints?.deleteBgPromptCategoryUrl,
                method: "DELETE",
                body: { catId: id }
            }),
            invalidatesTags: ['BgPromptCategory'],
        }),

        addBgPromptCategorySorting: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.bgPromptCategorySortingUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['BgPromptCategory'],
        }),

        /* ============================================================
           HOOKS FOR BACKGROUND PROMPTS (THE ITEMS)
        ============================================================ */
        getBgPrompts: builder.query({
            query: ({ page, limit, searchData, categoryId }) => ({
                url: `${apiEndpoints?.getBgPromptListUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}&category_id=${categoryId}`,
                method: 'GET',
            }),
            providesTags: ['BgPrompt'],
        }),

        addBgPrompt: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.addBgPromptUrl,
                method: "POST",
                body: data
            }),
            invalidatesTags: ['BgPrompt'],
        }),

        updateBgPrompt: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updateBgPromptUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['BgPrompt'],
        }),

        changeBgPromptStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.changeBgPromptStatusUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['BgPrompt'],
        }),

        deleteBgPrompt: builder.mutation({
            query: (promptId) => ({
                url: apiEndpoints?.deleteBgPromptUrl,
                method: "DELETE",
                body: { promptId }
            }),
            invalidatesTags: ['BgPrompt'],
        }),

        getBgPromptDetails: builder.query({
            query: (promptId) => ({
                url: `${apiEndpoints?.getBgPromptDetailsUrl}?promptId=${promptId}`,
                method: 'GET',
            }),
        }),
        getReviewStats: builder.query({
            query: (pastDay) => ({
                url: `${apiEndpoints?.reviewStatsUrl}${pastDay ? `?past_day=${pastDay}` : ''}`,
                method: 'GET',
            }),
        }),

        /* ============================================================
           HOOKS FOR HAIRCUT PROMPTS (flat list, no category, no add/status endpoints)
        ============================================================ */
        getHaircutPrompts: builder.query({
            query: ({ page, limit, searchData, type }) => ({
                url: `${apiEndpoints?.getHaircutPromptListUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}${type ? `&type=${type}` : ''}`,
                method: 'GET',
            }),
            providesTags: ['HaircutPrompt'],
        }),

        updateHaircutPrompt: builder.mutation({
            query: ({ id, title, prompt, type }) => ({
                url: apiEndpoints?.updateHaircutPromptUrl,
                method: "PUT",
                body: { id, title, prompt, type }
            }),
            invalidatesTags: ['HaircutPrompt'],
        }),

        deleteHaircutPrompt: builder.mutation({
            query: (id) => ({
                url: `${apiEndpoints?.deleteHaircutPromptUrl}?id=${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ['HaircutPrompt'],
        }),

        getHaircutPromptDetails: builder.query({
            query: (promptId) => ({
                url: `${apiEndpoints?.getHaircutPromptDetailsUrl}?id=${promptId}`,
                method: 'GET',
            }),
        }),

    })

});

export const {
    useGetUserDataQuery,
    useGetUserDataDetailsQuery,
    useUpdateUserStatusMutation,
    useGetDashboardDataQuery,
    useGetDashboardReportDataQuery,
    useGetSubscriptionChartDataQuery,
    // Category Hooks
    useGetCategoryDataQuery,
    useAddCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
    // Breed Hooks
    useGetBreedsDataQuery,
    useAddBreedMutation,
    useUpdateBreedMutation,
    useDeleteBreedMutation,

    // Style Hooks
    useGetStylesDataQuery,
    useAddStyleMutation,
    useUpdateStyleMutation,
    useDeleteStyleMutation,
    //
    useGetBgImagesDataQuery,
    useAddBgImageMutation,
    useUpdateBgImageMutation,
    useChangeBgImageStatusMutation,
    useDeleteBgImageMutation,

    // Sticker Hooks
    useGetStickersDataQuery,
    useAddStickerMutation,
    useUpdateStickerMutation,
    useChangeStickerStatusMutation,
    useDeleteStickerMutation,
    useGetStickerCategoriesQuery,
    useAddStickerCategoryMutation,
    useUpdateStickerCategoryMutation,
    useChangeStickerCategoryStatusMutation,
    useDeleteStickerCategoryMutation,
    useAddStickerCategorySortingMutation,
    useGetStickerPromptsQuery,
    useAddStickerPromptMutation,
    useUpdateStickerPromptMutation,
    useChangeStickerPromptStatusMutation,
    useDeleteStickerPromptMutation,
    useGetStickerPromptDetailsQuery,
    useLazyGetStickerPromptDetailsQuery,// Useful for clicking the "Eye" icon
    //
    useGetBackgroundCategoriesQuery,
    useAddBackgroundCategoryMutation,
    useAddBackgroundSubCategorySortingMutation,
    useUpdateBackgroundCategoryMutation,
    useChangeBgCategoryStatusMutation,
    useDeleteBackgroundCategoryMutation,
    useGetBackgroundCategoryDetailsQuery,

    //report
    useGetReportListQuery,
    useGetReportDetailsQuery,
    useDeleteReportMutation,
    useAcceptReportMutation,
    useRejectReportMutation,
    useGetUserReportListQuery,
    useGetUserReportDetailsQuery,
    useUserReportActionMutation,
    useDeleteUserReportMutation,

    //contact us
    useGetContactListQuery,
    useDeleteContactMutation,
    useReplyContactMutation,

    // New Category Hooks
    useGetBgPromptCategoriesQuery,
    useAddBgPromptCategoryMutation,
    useUpdateBgPromptCategoryMutation,
    useChangeBgPromptCategoryStatusMutation,
    useDeleteBgPromptCategoryMutation,
    useAddBgPromptCategorySortingMutation,

    // New Prompt Hooks
    useGetBgPromptsQuery,
    useAddBgPromptMutation,
    useUpdateBgPromptMutation,
    useChangeBgPromptStatusMutation,
    useDeleteBgPromptMutation,
    useLazyGetBgPromptDetailsQuery,
    useGetReviewStatsQuery,

    // Haircut Prompt Hooks
    useGetHaircutPromptsQuery,
    useUpdateHaircutPromptMutation,
    useDeleteHaircutPromptMutation,
    useGetHaircutPromptDetailsQuery,
    useLazyGetHaircutPromptDetailsQuery,
} = userApi;
